import dayjs from "dayjs"
import type { CSSProperties } from "react"
import { useMemo } from "react"
import { useGraphContext } from "../GraphContext"
import styles from "./GraphActivities.module.scss"
import {
  extraMarkerHeight,
  GraphActivityComponent,
} from "./GraphActivity"
import { useGraphActivities } from "./useGraphActivities"
import { useGraphActivitiesSpaceAnalysis } from "./useGraphActivitiesSpaceAnalysis"
import { useSortedGraphActivities } from "./useSortedGraphActivities"

export interface Props {
  minMgdl: number | undefined;
  maxMgdl: number | undefined;
  start: string;
  end: string;
}

// when this is true, each column of graph activities will leave gaps to
// prevent collisions with neighboring graph activities but because of the gaps
// each column can render fewer total activities
const gridShiftEnabled = false

// graph activities are events and entries that are not glucose readings
export function GraphActivities(props: Props) {
  const {
    start,
    end,
    minMgdl,
    maxMgdl,
  } = props

  const {
    graphActivityHeight,
    pointsPerHour,
  } = useGraphContext()

  const activities = useGraphActivities(
    start,
    end,
  )

  const width = useMemo(
    () => dayjs(end)
      .diff(
        start,
        "hours",
        true,
      ) * pointsPerHour,
    [
      end,
      start,
      pointsPerHour,
    ],
  )

  const {
    glucoseRows,
    rowsAboveGlucose,
    rowsBelowGlucose,
  } = useGraphActivitiesSpaceAnalysis(
    start,
    gridShiftEnabled,
    minMgdl,
    maxMgdl,
  )

  const [
    graphActivitiesAbove,
    graphActivitiesBelow,
  ] = useSortedGraphActivities(
    activities,
    gridShiftEnabled ? Math.ceil(rowsAboveGlucose / 2) : rowsAboveGlucose,
    gridShiftEnabled ? Math.ceil(rowsBelowGlucose / 2) : rowsBelowGlucose,
  )

  const topStyle: CSSProperties = useMemo(
    () => ({
      height: graphActivityHeight * rowsAboveGlucose,
      // this leaves enough room above the highest activity to show the time marker
      marginTop: extraMarkerHeight / 2,
      gap: gridShiftEnabled ? graphActivityHeight : undefined,

    }),
    [
      rowsAboveGlucose,
      graphActivityHeight,
    ],
  )

  const middleStyle: CSSProperties = useMemo(
    () => ({
      height: graphActivityHeight * glucoseRows,
    }),
    [
      glucoseRows,
      graphActivityHeight,
    ],
  )

  const bottomStyle: CSSProperties = useMemo(
    () => ({
      height: graphActivityHeight * rowsBelowGlucose,
      // this leaves enough room above the lowest activity to show the time marker
      marginBottom: extraMarkerHeight / 2,
      gap: gridShiftEnabled ? graphActivityHeight : undefined,
    }),
    [
      rowsBelowGlucose,
      graphActivityHeight,
    ],
  )

  return (
    <div
      className={styles.container}
      style={{ width }}
    >
      <div
        className={styles.top}
        id="top-graph-range"
        style={topStyle}
      >
        {graphActivitiesAbove.map((activity) => (
          <GraphActivityComponent
            key={activity.key}
            activity={activity}
            containerStartTime={start}
          />
        ))}
      </div>
      <div
        id="glucose-spacing-cell"
        className={styles.middle}
        style={middleStyle}
      />
      <div
        className={styles.bottom}
        style={bottomStyle}
        id="bottom-graph-range"
      >
        {graphActivitiesBelow.map((activity) => (
          <GraphActivityComponent
            key={activity.key}
            activity={activity}
            containerStartTime={start}
          />
        ))}
      </div>
    </div>
  )
}
